import { AnimatePresence, motion } from 'motion/react';
import { useContext, useEffect, useMemo, useState } from 'react';
import AppliedFiltersContext from '~/context/common/AppliedFiltersContext';
import FacetDataContext from '~/context/common/FacetDataContext';
import type { MakeFacet } from '~/contracts/common';
import ParentModel from './ParentModel';
import Checkbox from '../Checkbox';

// Because there is a delay between toggling a make on and when we start
// fetching facets, we want to delay showing the models. Essentially we are
// waiting for JavaScript processing to happen, which hopefully doesn't longer
// than our wait.
function useSlowShow(shouldShow: boolean, waitMilliseconds: number): boolean {
  const [show, setShow] = useState(shouldShow);

  useEffect(() => {
    let t: number;

    if (shouldShow) {
      t = window.setTimeout(() => {
        setShow(true);
      }, waitMilliseconds);
    } else {
      setShow(false);
    }

    return () => {
      clearTimeout(t);
    };
  }, [shouldShow, waitMilliseconds]);

  return show;
}

// When a make is toggled on, we want to wait until the facets are loaded before
// we show the models. We only want to hide models for the most recently toggled
// make.
function useWaitForLoadedOnChanged(
  isSelected: boolean,
  isLoaded: boolean
): boolean {
  const [isLoading, setLoading] = useState(false);

  useEffect(() => {
    if (isSelected) setLoading(true);
  }, [isSelected]);

  useEffect(() => {
    if (isLoaded) setLoading(false);
  }, [isLoaded]);

  return !isLoading;
}

type OwnProps = Omit<MakeFacet, 'key'> & {
  make: string;
};

export default function Make({ count, make, parentModels = [] }: OwnProps) {
  const { appliedFilters, updateMakes } = useContext(AppliedFiltersContext);
  const { facetData, showInventoryCounts } = useContext(FacetDataContext);

  const isSelected = useMemo(
    () =>
      appliedFilters?.makes?.some(appliedMake => appliedMake.name === make) ??
      false,
    [appliedFilters.makes, make]
  );

  const isSelectedLazy = useSlowShow(isSelected, 100);
  const isLoaded = useWaitForLoadedOnChanged(isSelected, !facetData.isLoading);

  const showModels = isSelectedLazy && isLoaded && parentModels.length > 0;

  return (
    <>
      <Checkbox
        facet="make"
        filter={make}
        id={`make-${make}`}
        checked={isSelected}
        key={make}
        onChange={() => updateMakes({ name: make })}
        label={
          showInventoryCounts ? `${make} (${count.toLocaleString()})` : make
        }
      />
      <AnimatePresence>
        {showModels && (
          <motion.div
            initial={{ height: 0 }}
            animate={{
              height: 'auto'
            }}
            exit={{ height: 0 }}
            transition={{ duration: 0.5 }}
            className="overflow-hidden px-3xl"
          >
            <p className="mb-xl text-text-weak t-body-sm">Models</p>
            <div className="grid gap-xl pb-3xl">
              {parentModels.map(model => (
                <ParentModel
                  key={`${make} ${model.key} ${model.modelIds.join('-')}`}
                  make={make}
                  model={model.key}
                  count={model.count}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
